/*!
 * chord-finder/diagram.js — chord shapes as SVG.
 *
 * Browser global `window.ChordDiagram`; pure string building, no DOM reads, so
 * it is safe to call in a loop while rendering and cheap enough to re-run on
 * every capo/transpose change (measured: 12 diagrams in well under a frame).
 *
 * ── WHY SVG STRINGS AND NOT CANVAS ───────────────────────────────────────
 * A chord diagram has to survive three things a canvas bitmap does not: the
 * light/dark theme switch, the browser's print path (Premium exports a chord
 * sheet), and a reader zooming in to see which finger goes where. Colours are
 * `currentColor` and CSS custom properties, so the theme drives them with no
 * redraw at all.
 *
 * ── LEFT-HANDED IS A MIRROR, NOT A SECOND CODE PATH ──────────────────────
 * Handedness flips the string order at draw time only. Doing it in the
 * generator instead would have produced shapes whose fret strings ("x32010")
 * disagreed with every chord book on the planet.
 */
(function (root) {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Fretboard diagram.
   *
   * @param {object} shape  from ChordFingering.shapes()
   * @param {object} opts   { width, height, lefty, showFingers, frets, capo }
   * @returns {string} SVG markup
   */
  function fretboard(shape, opts) {
    opts = opts || {};
    var W = opts.width || 74;
    var H = opts.height || 84;
    var lefty = !!opts.lefty;
    var showFingers = opts.showFingers !== false;
    var nFrets = opts.frets || 5;

    var frets = shape.frets.slice();
    var fingers = (shape.fingers || []).slice();
    if (lefty) { frets.reverse(); fingers.reverse(); }
    var nStr = frets.length;

    /*
     * Where the window starts. A shape reaching no further than the 4th fret is
     * drawn from the nut, because that is how a reader locates it — "open
     * position" is a landmark, not a coordinate. Higher up, the window starts
     * at the shape's own lowest fretted fret and says so with a number, which
     * is the only way a five-fret picture can address a fourteen-fret neck.
     */
    var maxFret = 0, i;
    for (i = 0; i < nStr; i++) if (frets[i] > maxFret) maxFret = frets[i];
    var fromNut = maxFret <= nFrets - 1;
    var base = fromNut ? 0 : (shape.minFret - 1);

    var padL = 9, padR = 7, padT = 14, padB = 2;
    var gridW = W - padL - padR;
    var gridH = H - padT - padB;
    var dx = nStr > 1 ? gridW / (nStr - 1) : 0;
    var dy = gridH / nFrets;

    var s = '<svg class="cf-fb" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H +
            '" xmlns="' + NS + '" aria-hidden="true" focusable="false">';

    // Nut (thick) or the fret number that replaces it.
    if (fromNut) {
      s += '<rect x="' + padL + '" y="' + (padT - 2.4) + '" width="' + gridW + '" height="2.6" rx="1" fill="currentColor"/>';
    } else {
      s += '<text x="' + (padL - 3.5) + '" y="' + (padT + dy * 0.72) + '" class="cf-fb-pos" ' +
           'text-anchor="end" font-size="8.5">' + esc(base + 1) + '</text>';
    }

    // Frets and strings.
    for (i = 1; i <= nFrets; i++) {
      s += '<line x1="' + padL + '" y1="' + (padT + dy * i) + '" x2="' + (padL + gridW) +
           '" y2="' + (padT + dy * i) + '" class="cf-fb-line"/>';
    }
    for (i = 0; i < nStr; i++) {
      var x = padL + dx * i;
      s += '<line x1="' + x + '" y1="' + padT + '" x2="' + x + '" y2="' + (padT + gridH) + '" class="cf-fb-line"/>';
    }

    // Open / muted markers above the nut.
    for (i = 0; i < nStr; i++) {
      var xm = padL + dx * i;
      if (frets[i] < 0) {
        var r = 2.6;
        s += '<path d="M' + (xm - r) + ' ' + (padT - 9 - r) + 'L' + (xm + r) + ' ' + (padT - 9 + r) +
             'M' + (xm + r) + ' ' + (padT - 9 - r) + 'L' + (xm - r) + ' ' + (padT - 9 + r) +
             '" class="cf-fb-x"/>';
      } else if (frets[i] === 0) {
        s += '<circle cx="' + xm + '" cy="' + (padT - 9) + '" r="2.7" class="cf-fb-o"/>';
      }
    }

    // Barre first, so finger dots sit on top of it.
    if (shape.barre && shape.barre.fret > base) {
      var bFrom = shape.barre.from, bTo = shape.barre.to;
      if (lefty) { var t = nStr - 1 - bTo; bTo = nStr - 1 - bFrom; bFrom = t; }
      var by = padT + dy * (shape.barre.fret - base - 0.5);
      var bx1 = padL + dx * bFrom, bx2 = padL + dx * bTo;
      s += '<rect x="' + (bx1 - 4.4) + '" y="' + (by - 4.4) + '" width="' + (bx2 - bx1 + 8.8) +
           '" height="8.8" rx="4.4" class="cf-fb-barre"/>';
    }

    // Fretted notes.
    for (i = 0; i < nStr; i++) {
      if (frets[i] <= 0) continue;
      var rel = frets[i] - base;
      if (rel < 1 || rel > nFrets) continue;
      var cx = padL + dx * i, cy = padT + dy * (rel - 0.5);
      s += '<circle cx="' + cx + '" cy="' + cy + '" r="4.4" class="cf-fb-dot"/>';
      if (showFingers && fingers[i]) {
        s += '<text x="' + cx + '" y="' + (cy + 2.6) + '" class="cf-fb-fin" text-anchor="middle" ' +
             'font-size="7">' + esc(fingers[i]) + '</text>';
      }
    }

    // Capo bar at the nut — the reader is fingering ABOVE it, so it belongs in
    // the picture rather than in a caption they have to hold in their head.
    if (opts.capo && fromNut) {
      s += '<rect x="' + (padL - 3) + '" y="' + (padT - 5.6) + '" width="' + (gridW + 6) +
           '" height="4.2" rx="2.1" class="cf-fb-capo"/>';
    }

    return s + '</svg>';
  }

  /*
   * Keyboard diagram — one octave from C.
   *
   * Register is deliberately absent: the chromagram discarded octave and
   * voicing by construction, so drawing a specific inversion would be printing
   * information the analysis never had. One octave with the chord tones lit is
   * exactly what is known and no more.
   */
  var WHITE_PC = [0, 2, 4, 5, 7, 9, 11];
  var BLACK = [{ pc: 1, after: 0 }, { pc: 3, after: 1 }, { pc: 6, after: 3 }, { pc: 8, after: 4 }, { pc: 10, after: 5 }];

  function keyboard(keys, opts) {
    opts = opts || {};
    var W = opts.width || 74;
    var H = opts.height || 84;
    var on = {}, i;
    for (i = 0; i < keys.pcs.length; i++) on[keys.pcs[i]] = 1;

    var padT = 14;
    var kh = H - padT - 2;
    var kw = W / 7;
    var s = '<svg class="cf-kb" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H +
            '" xmlns="' + NS + '" aria-hidden="true" focusable="false">';

    for (i = 0; i < 7; i++) {
      var pc = WHITE_PC[i];
      var cls = 'cf-kb-w' + (on[pc] ? (pc === keys.root ? ' root' : ' on') : '');
      s += '<rect x="' + (i * kw) + '" y="' + padT + '" width="' + (kw - 0.8) + '" height="' + kh +
           '" rx="1.6" class="' + cls + '"/>';
    }
    for (i = 0; i < BLACK.length; i++) {
      var b = BLACK[i];
      var bw = kw * 0.58;
      var bx = (b.after + 1) * kw - bw / 2 - 0.4;
      var bcls = 'cf-kb-b' + (on[b.pc] ? (b.pc === keys.root ? ' root' : ' on') : '');
      s += '<rect x="' + bx + '" y="' + padT + '" width="' + bw + '" height="' + (kh * 0.62) +
           '" rx="1.4" class="' + bcls + '"/>';
    }
    return s + '</svg>';
  }

  root.ChordDiagram = { fretboard: fretboard, keyboard: keyboard, esc: esc };
})(typeof self !== 'undefined' ? self : this);
